import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { config, response } = error;
    if (response?.status === 402 && typeof response.data?.detail === "string" && response.data.detail.includes("trial")) {
      window.dispatchEvent(new CustomEvent("d20-readonly", { detail: response.data.detail }));
    }
    if (response?.status === 401 && config && !config._retried && !config.url.includes("/auth/")) {
      config._retried = true;
      try {
        refreshing = refreshing || axios.post(`${API}/auth/refresh`, null, { withCredentials: true }).finally(() => { refreshing = null; });
        await refreshing;
        return api(config);
      } catch (e) {
        window.dispatchEvent(new Event("d20-logout"));
      }
    }
    return Promise.reject(error);
  }
);

export const formatApiError = (detail, fallback = "Something went wrong. Please try again.") => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogin = (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data);
export const authRegister = (body) => api.post("/auth/register", body).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authForgot = (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data);
export const authReset = (token, password) => api.post("/auth/reset-password", { token, password }).then((r) => r.data);
export const authInviteInfo = (token) => api.get(`/auth/invite/${token}`).then((r) => r.data);
export const updateProfile = (body) => api.put("/auth/profile", body).then((r) => r.data);
export const getMyDevices = () => api.get("/auth/devices").then((r) => r.data);
export const forgetDevice = (id) => api.delete(`/auth/devices/${id}`).then((r) => r.data);
export const getTeam = () => api.get("/team").then((r) => r.data);
export const renameOrg = (name) => api.put("/team/org", { name }).then((r) => r.data);
export const inviteTeammate = (email, role) => api.post("/team/invites", { email, role }).then((r) => r.data);
export const revokeInvite = (id) => api.delete(`/team/invites/${id}`).then((r) => r.data);
export const changeRole = (id, role) => api.put(`/team/users/${id}/role`, { role }).then((r) => r.data);
export const removeTeammate = (id) => api.delete(`/team/users/${id}`).then((r) => r.data);
export const getReviewers = () => api.get("/reviewers").then((r) => r.data);

export const getStats = (user) => api.get("/stats", { params: user ? { user } : {} }).then((r) => r.data);
export const getSpendAnalytics = (scope = "all", months = 6) =>
  api.get("/analytics/spend", { params: { scope, months } }).then((r) => r.data);
export const forgetSupplierMemory = (supplierKey, glCode, user) =>
  api
    .delete(`/suppliers/${encodeURIComponent(supplierKey)}/memory`, { params: { gl_code: glCode || undefined, user } })
    .then((r) => r.data);
export const assignInvoice = (id, assignee, user, note) =>
  api.post(`/invoices/${id}/assign`, { assignee, user, note: note || null }).then((r) => r.data);
export const getExportPreview = (id, system) =>
  api.get(`/invoices/${id}/export-preview`, { params: { system } }).then((r) => r.data);
export const getDueAlerts = (days = 7) => api.get("/alerts/due", { params: { days } }).then((r) => r.data);
export const simulateRules = (min_confidence, max_amount) =>
  api.get("/rules/simulate", { params: { min_confidence, max_amount } }).then((r) => r.data);
export const markPaid = (id, body) => api.post(`/invoices/${id}/pay`, body).then((r) => r.data);
export const unmarkPaid = (id, user) => api.delete(`/invoices/${id}/pay`, { params: { user } }).then((r) => r.data);

export const getSupplierContact = (key) => api.get(`/suppliers/${encodeURIComponent(key)}/contact`).then((r) => r.data);
export const saveSupplierContact = (key, body) => api.put(`/suppliers/${encodeURIComponent(key)}/contact`, body).then((r) => r.data);
export const getNotifications = (params = {}) =>
  api.get("/notifications", { params }).then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] || r.data.length) }));
export const getNotificationsStatus = () => api.get("/notifications/status").then((r) => r.data);
export const notifyInvoice = (id, event, user) => api.post(`/invoices/${id}/notify`, { event, user }).then((r) => r.data);

export const getPaymentRunCandidates = () => api.get("/payment-runs/candidates").then((r) => r.data);
export const createPaymentRun = (body) => api.post("/payment-runs", body).then((r) => r.data);
export const getPaymentRuns = () => api.get("/payment-runs").then((r) => r.data);
export const getPaymentRun = (id) => api.get(`/payment-runs/${id}`).then((r) => r.data);

export const getDigest = (user, week = "this") => api.get("/digest", { params: { user, week } }).then((r) => r.data);
export const bulkApprove = (ids, user, comment) =>
  api.post("/invoices/bulk-approve", { ids, user, comment: comment || null }).then((r) => r.data);
export const splitLineItem = (invoiceId, itemId, parts, user) =>
  api.post(`/invoices/${invoiceId}/line-item/${itemId}/split`, { parts, user }).then((r) => r.data);
export const getGmailStatus = () => api.get("/gmail/status").then((r) => r.data);
export const getInboundAddress = () => api.get("/inbound/address").then((r) => r.data);
export const regenerateInboundAddress = () => api.post("/inbound/address/regenerate").then((r) => r.data);
export const lockInboundAddress = (lock) => api.post(`/inbound/address/${lock ? "lock" : "unlock"}`).then((r) => r.data);
export const getInboundLog = (limit = 20) => api.get("/inbound/log", { params: { limit } }).then((r) => r.data);
export const getOnboarding = () => api.get("/onboarding").then((r) => r.data);
export const dismissOnboarding = () => api.post("/onboarding/dismiss").then((r) => r.data);
export const restoreOnboarding = () => api.post("/onboarding/restore").then((r) => r.data);
export const getPlatformAiCosts = () => api.get("/billing/platform/ai-costs").then((r) => r.data);
export const getStripeHealth = () => api.get("/billing/platform/stripe-health").then((r) => r.data);
export const getGoLive = () => api.get("/billing/platform/go-live").then((r) => r.data);
export const syncStripeCatalog = () => api.post("/billing/platform/sync-catalog").then((r) => r.data);
export const setPlatformTax = (vat_registered) => api.put("/billing/platform/tax", { vat_registered }).then((r) => r.data);
export const tickGoLive = (key, done) => api.put(`/billing/platform/go-live/${key}`, { done }).then((r) => r.data);
export const updatePlatformSettings = (body) => api.put("/billing/platform/settings", body).then((r) => r.data);
export const runMarginCheck = () => api.post("/billing/platform/margin-check").then((r) => r.data);
export const setComplimentary = (orgId, plan, note) => api.put(`/billing/platform/orgs/${orgId}/complimentary`, { plan, note }).then((r) => r.data);
export const extendTrial = (orgId, days) => api.put(`/billing/platform/orgs/${orgId}/trial`, { days }).then((r) => r.data);
export const getSupplierInvitePreview = (key, note) => api.get(`/suppliers/${encodeURIComponent(key)}/invite/preview`, { params: { note: note || undefined } }).then((r) => r.data);
export const sendSupplierInvite = (key, note, user) => api.post(`/suppliers/${encodeURIComponent(key)}/invite`, { note, user }).then((r) => r.data);
export const inviteAllSuppliers = (note, user) => api.post("/suppliers/invite-all", { note, user }).then((r) => r.data);
export const getGmailHealth = () => api.get("/gmail/health").then((r) => r.data);
export const getTrustedSenders = () => api.get("/gmail/trusted-senders").then((r) => r.data);
export const addTrustedSender = (value, user, note) => api.post("/gmail/trusted-senders", { value, user, note }).then((r) => r.data);
export const removeTrustedSender = (id, user) => api.delete(`/gmail/trusted-senders/${id}`, { params: { user } }).then((r) => r.data);
export const getSkippedSenders = () => api.get("/gmail/skipped").then((r) => r.data);
export const getDuplicateCount = () => api.get("/invoices/duplicates/count").then((r) => r.data);
export const trustSkipped = (messageId, scope, user) => api.post(`/gmail/skipped/${messageId}/trust`, { scope, user }).then((r) => r.data);
export const dismissSkipped = (messageId) => api.delete(`/gmail/skipped/${messageId}`).then((r) => r.data);
export const getAlertsStatus = () => api.get("/alerts/status").then((r) => r.data);
export const sendTestAlert = () => api.post("/alerts/test").then((r) => r.data);
export const getFxStatus = () => api.get("/fx/status").then((r) => r.data);
export const refreshFx = (force = false) => api.post("/fx/refresh", null, { params: { force } }).then((r) => r.data);
export const getChases = (status = "open") => api.get("/chases", { params: { status } }).then((r) => r.data);
export const waiveChase = (id, user, note) => api.post(`/chases/${id}/waive`, { user, note }).then((r) => r.data);
export const reopenChase = (id, user) => api.post(`/chases/${id}/reopen`, { user }).then((r) => r.data);
export const runSlaNudges = () => api.post("/alerts/sla/run").then((r) => r.data);
export const createClosePack = (month, user) => api.post("/close-packs", { month, user }).then((r) => r.data);
export const emailClosePack = (id, user) => api.post(`/close-packs/${id}/email`, { user }).then((r) => r.data);
export const getClosePackSchedule = () => api.get("/close-packs/schedule").then((r) => r.data);
export const runClosePackSchedule = (force = false) => api.post("/close-packs/schedule/run", null, { params: { force } }).then((r) => r.data);
export const getClosePacks = () => api.get("/close-packs").then((r) => r.data);
export const closePackUrl = (id) => `${API}/close-packs/${id}/download`;
export const overrideFx = (id, body) => api.put(`/invoices/${id}/fx`, body).then((r) => r.data);
export const revertFx = (id, user) => api.delete(`/invoices/${id}/fx`, { params: { user } }).then((r) => r.data);
export const getStatements = (key) => api.get(`/suppliers/${encodeURIComponent(key)}/statements`).then((r) => r.data);
export const getStatement = (id) => api.get(`/statements/${id}`).then((r) => r.data);
export const deleteStatement = (id) => api.delete(`/statements/${id}`).then((r) => r.data);
export const requestCopies = (id, user) => api.post(`/statements/${id}/request-copies`, { user }).then((r) => r.data);
export const uploadStatement = (key, { file, text }, user) => {
  const form = new FormData();
  if (file) form.append("file", file);
  if (text) form.append("text", text);
  form.append("user", user);
  return api.post(`/suppliers/${encodeURIComponent(key)}/statements`, form, { timeout: 180000 }).then((r) => r.data);
};
export const gmailLoginUrl = (user) => `${API}/oauth/gmail/login?user=${encodeURIComponent(user)}`;
export const gmailSync = (user) => api.post("/gmail/sync", null, { params: { user } }).then((r) => r.data);
export const gmailDisconnect = (user) => api.post("/gmail/disconnect", null, { params: { user } }).then((r) => r.data);
export const gmailAutoPoll = (enabled, user) =>
  api.put("/gmail/auto-poll", null, { params: { enabled, user } }).then((r) => r.data);
export const getFxExposure = (month) => api.get("/analytics/fx", { params: month ? { month } : {} }).then((r) => r.data);
export const getInvoices = (params = {}) => api.get("/invoices", { params }).then((r) => r.data);
export const getInvoicesPaged = (params = {}) =>
  api.get("/invoices", { params }).then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] || r.data.length) }));
export const getInvoice = (id) => api.get(`/invoices/${id}`).then((r) => r.data);
export const getInvoicePages = (id) => api.get(`/invoices/${id}/pages`).then((r) => r.data);
export const correctInvoiceFields = (id, body) => api.patch(`/invoices/${id}/fields`, body).then((r) => r.data);
export const editLineItem = (id, itemId, body) => api.patch(`/invoices/${id}/line-item/${itemId}/fields`, body).then((r) => r.data);
export const getInvoiceFileInfo = (id) => api.get(`/invoices/${id}/file-info`).then((r) => r.data);
export const splitInvoice = (id, groups, user) => api.post(`/invoices/${id}/split`, { groups, user }).then((r) => r.data);
export const mergeInvoices = (invoice_ids, user) => api.post("/invoices/merge", { invoice_ids, user }).then((r) => r.data);
export const getPublicAccuracy = () => api.get("/public/accuracy").then((r) => r.data);
export const getAccuracyReport = (months = 6) => api.get("/reports/accuracy", { params: { months } }).then((r) => r.data);
export const getCategories = () => api.get("/categories").then((r) => r.data);
export const getAudit = (invoiceId) =>
  api.get("/audit", { params: invoiceId ? { invoice_id: invoiceId } : {} }).then((r) => r.data);
export const getAuditPaged = (params = {}) =>
  api.get("/audit", { params }).then((r) => ({ items: r.data, total: Number(r.headers["x-total-count"] || r.data.length) }));
export const getPermissions = () => api.get("/permissions").then((r) => r.data);
export const setUserPermission = (userId, key, value) => api.put(`/permissions/users/${userId}`, { key, value }).then((r) => r.data);
export const setUserRole = (userId, role) => api.put(`/permissions/users/${userId}/role`, { role }).then((r) => r.data);
export const savePolicies = (body) => api.put("/permissions/policies", body).then((r) => r.data);
export const getAuditPeriods = () => api.get("/audit/periods").then((r) => r.data);
export const auditExportUrl = ({ format, period, anchor, q, download }) => {
  const p = new URLSearchParams({ format, period: period || "all" });
  if (period && period !== "all" && anchor) p.set("anchor", anchor);
  if (q) p.set("q", q);
  if (download) p.set("download", "1");
  return `${API}/audit/export?${p.toString()}`;
};
export const getSuppliers = () => api.get("/suppliers").then((r) => r.data);

export const getComments = (invoiceId) => api.get(`/invoices/${invoiceId}/comments`).then((r) => r.data);
export const addComment = (invoiceId, text, user) =>
  api.post(`/invoices/${invoiceId}/comments`, { text, user }).then((r) => r.data);
export const editComment = (commentId, text, user) =>
  api.put(`/comments/${commentId}`, { text, user }).then((r) => r.data);
export const deleteComment = (commentId, user) =>
  api.delete(`/comments/${commentId}`, { params: { user } }).then((r) => r.data);

export const extractEmail = (text, user) =>
  api.post("/invoices/extract-email", { text, user }).then((r) => r.data);

export const extractFile = (file, user) => {
  const form = new FormData();
  form.append("file", file);
  form.append("user", user);
  return api.post("/invoices/extract-file", form).then((r) => r.data);
};

export const updateLineItem = (invoiceId, itemId, gl_code, user) =>
  api.patch(`/invoices/${invoiceId}/line-item/${itemId}`, { gl_code, user }).then((r) => r.data);

export const approveInvoice = (id, user, comment) =>
  api.post(`/invoices/${id}/approve`, { user, comment }).then((r) => r.data);

export const rejectInvoice = (id, user, comment) =>
  api.post(`/invoices/${id}/reject`, { user, comment }).then((r) => r.data);

export const resolveDuplicate = (id, user, decision, comment) =>
  api.post(`/invoices/${id}/duplicate/resolve`, { user, decision, comment }).then((r) => r.data);

export const deleteInvoice = (id) => api.delete(`/invoices/${id}`).then((r) => r.data);

export const exportInvoice = (id, system, user) =>
  api.post(`/invoices/${id}/export`, { system, user }).then((r) => r.data);

export const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const seedData = () => api.post("/seed").then((r) => r.data);

export const getSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (s) => api.put("/settings", s).then((r) => r.data);

export const createCategory = (cat) => api.post("/categories", cat).then((r) => r.data);
export const updateCategory = (code, cat) => api.put(`/categories/${encodeURIComponent(code)}`, cat).then((r) => r.data);
export const deleteCategory = (code, replacement, user) =>
  api
    .delete(`/categories/${encodeURIComponent(code)}`, { params: { replacement: replacement || undefined, user } })
    .then((r) => r.data);
export const getCategoryUsage = (code) => api.get(`/categories/${encodeURIComponent(code)}/usage`).then((r) => r.data);
export const resetCategories = () => api.post("/categories/reset").then((r) => r.data);
export const importCategories = (file, mode) => {
  const form = new FormData();
  form.append("file", file);
  form.append("mode", mode);
  return api.post("/categories/import", form).then((r) => r.data);
};

let BASE_CURRENCY = "GBP";
export const setBaseCurrency = (c) => { if (c) BASE_CURRENCY = c; };
export const getBaseCurrency = () => BASE_CURRENCY;

export const money = (v, currency) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || BASE_CURRENCY }).format(v || 0);

export const baseMoney = (inv) =>
  inv?.base_total != null && inv.currency !== inv.base_currency ? money(inv.base_total, inv.base_currency) : null;

export const STATUS_META = {
  pending_review: { label: "Pending Review", cls: "bg-amber-500/15 text-amber-500 border-amber-500/30" },
  auto_coded: { label: "Auto-Coded", cls: "bg-indigo-500/15 text-indigo-400 border-indigo-500/30" },
  approved: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30" },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-500 border-red-500/30" },
};

// ---- Xero direct sync ----
export const XERO_CONNECT_URL = `${API}/integrations/xero/connect`;
export const xeroConnectLink = () => api.get("/integrations/xero/connect", { params: { json: true } }).then((r) => r.data.url);
export const getXeroStatus = () => api.get("/integrations/xero/status").then((r) => r.data);
export const disconnectXero = () => api.delete("/integrations/xero/disconnect").then((r) => r.data);
export const chooseXeroTenant = (tenant_id) => api.put("/integrations/xero/tenant", { tenant_id }).then((r) => r.data);
export const getXeroMapping = () => api.get("/integrations/xero/mapping").then((r) => r.data);
export const saveXeroMapping = (map) => api.put("/integrations/xero/mapping", { map }).then((r) => r.data);
export const suggestXeroMapping = () => api.post("/integrations/xero/mapping/suggest").then((r) => r.data);
export const importXeroAccounts = () => api.post("/integrations/xero/accounts/import").then((r) => r.data);
export const syncInvoiceToXero = (id, user) => api.post(`/invoices/${id}/xero-sync`, { user }).then((r) => r.data);
export const syncApprovedToXero = (user) => api.post("/integrations/xero/sync-approved", { user }).then((r) => r.data);
export const setXeroSettings = (auto_sync) => api.put("/integrations/xero/settings", { auto_sync }).then((r) => r.data);
export const pullXeroPayments = () => api.post("/integrations/xero/pull-payments").then((r) => r.data);
export const getFxGains = (month) => api.get("/reports/fx-gains", { params: { month } }).then((r) => r.data);
export const ledgerRefresh = (id) => api.post(`/invoices/${id}/ledger-refresh`).then((r) => r.data);
export const getXeroReport = (month) => api.get("/integrations/xero/report", { params: { month } }).then((r) => r.data);
export const setXeroImportBills = (import_bills) => api.put("/integrations/xero/settings", { import_bills }).then((r) => r.data);
export const importXeroBills = () => api.post("/integrations/xero/import-bills").then((r) => r.data);
export const setXeroDriftAlerts = (drift_alerts) => api.put("/integrations/xero/settings", { drift_alerts }).then((r) => r.data);
export const ackXeroDrift = (id, user, note) => api.post(`/invoices/${id}/xero-drift/ack`, { user, note }).then((r) => r.data);
export const getIntegrationsHealth = () => api.get("/integrations/health").then((r) => r.data);
export const getXeroBankAccounts = (force = false) => api.get("/integrations/xero/bank-accounts", { params: force ? { force: true } : {} }).then((r) => r.data);
export const setXeroPaymentAccount = (payment_account_id) => api.put("/integrations/xero/settings", { payment_account_id }).then((r) => r.data);
export const pushXeroPayment = (id) => api.post(`/invoices/${id}/xero-payment`).then((r) => r.data);

// ---- Generic accounting connectors (FreshBooks today; Zoho Books / Sage follow the same routes) ----
export const accountingApi = (provider) => ({
  provider,
  connectUrl: `${API}/integrations/${provider}/connect`,
  connectLink: (extra = {}) => api.get(`/integrations/${provider}/connect`, { params: { json: true, ...extra } }).then((r) => r.data.url),
  status: () => api.get(`/integrations/${provider}/status`).then((r) => r.data),
  disconnect: () => api.delete(`/integrations/${provider}/disconnect`).then((r) => r.data),
  chooseTenant: (tenant_id) => api.put(`/integrations/${provider}/tenant`, { tenant_id }).then((r) => r.data),
  mapping: () => api.get(`/integrations/${provider}/mapping`).then((r) => r.data),
  saveMapping: (map) => api.put(`/integrations/${provider}/mapping`, { map }).then((r) => r.data),
  suggest: () => api.post(`/integrations/${provider}/mapping/suggest`).then((r) => r.data),
  setSettings: (body) => api.put(`/integrations/${provider}/settings`, body).then((r) => r.data),
  syncApproved: (user) => api.post(`/integrations/${provider}/sync-approved`, { user }).then((r) => r.data),
  syncInvoice: (id, user) => api.post(`/invoices/${id}/${provider}-sync`, { user }).then((r) => r.data),
  pullPayments: () => api.post(`/integrations/${provider}/pull-payments`).then((r) => r.data),
  importBills: () => api.post(`/integrations/${provider}/import-bills`).then((r) => r.data),
  report: (month) => api.get(`/integrations/${provider}/report`, { params: month ? { month } : {} }).then((r) => r.data),
  ackDrift: (id, user, note) => api.post(`/invoices/${id}/${provider}-drift/ack`, { user, note }).then((r) => r.data),
  pushPayment: (id) => api.post(`/invoices/${id}/${provider}-payment`).then((r) => r.data),
  retryPayments: () => api.post(`/integrations/${provider}/retry-payments`).then((r) => r.data),
  bankAccounts: (force = false) => api.get(`/integrations/${provider}/bank-accounts`, { params: force ? { force: true } : {} }).then((r) => r.data),
});
export const getXeroTracking = (force = false) => api.get("/integrations/xero/tracking", { params: force ? { force: true } : {} }).then((r) => r.data);
export const setInvoiceTracking = (id, tracking, user) => api.put(`/invoices/${id}/tracking`, { tracking, user }).then((r) => r.data);
export const setLineTracking = (id, itemId, tracking, user) => api.put(`/invoices/${id}/line-item/${itemId}/tracking`, { tracking, user }).then((r) => r.data);

// ---- Web push ----
export const getPushPublicKey = () => api.get("/push/public-key").then((r) => r.data);
export const getPushStatus = (endpoint) => api.get("/push/status", { params: endpoint ? { endpoint } : {} }).then((r) => r.data);
export const subscribePush = (subscription) => api.post("/push/subscribe", { subscription, user_agent: navigator.userAgent }).then((r) => r.data);
export const unsubscribePush = (endpoint) => api.delete("/push/subscribe", { data: { endpoint } }).then((r) => r.data);
export const sendTestPush = () => api.post("/push/test").then((r) => r.data);

export const extractPhotos = (files, user) => {
  const fd = new FormData();
  files.forEach((f, i) => fd.append("files", f, f.name || `page-${i + 1}.jpg`));
  fd.append("user", user);
  return api.post("/invoices/extract-photos", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }).then((r) => r.data);
};
